/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { format } from '@msdyn365-commerce-modules/utilities';
import { getGiftCardInquiryAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/StoreOperationsDataActions.g';
import { observer } from 'mobx-react';
import * as React from 'react';
import CallToAction from '../../components/call-to-action';
import Form from '../../components/form/Form';
import { IGiftCardBalanceData } from './gift-card-balance.data';
import { IGiftCardBalanceProps } from './gift-card-balance.props.autogenerated';

interface IGiftCardFormState {
    isFetchingGiftCard: boolean;
    giftCardNumber: string;
    errorMessage: string;
    giftCardMessage: string | undefined;
    inputState: boolean;
}

/**
 *
 * GiftCardBalance component
 * @extends {React.Component<IGiftCardBalanceProps<IGiftCardBalanceData>,IGiftCardFormState>}
 */

@observer
class GiftCardBalance extends React.Component<IGiftCardBalanceProps<IGiftCardBalanceData>, IGiftCardFormState> {
    public state: IGiftCardFormState = {
        isFetchingGiftCard: false,
        giftCardNumber: '',
        errorMessage: '',
        giftCardMessage: '',
        inputState: true
    };
    private _giftCardNumberInputRef: React.RefObject<HTMLInputElement>;
    constructor(props: IGiftCardBalanceProps<IGiftCardBalanceData>) {
        super(props);
        this._giftCardNumberInputRef = React.createRef();
        this._getGiftCardBalance = this._getGiftCardBalance.bind(this);
        this._getUnauthorizedTagMessage = this._getUnauthorizedTagMessage.bind(this);
        this._handleSubmit = this._handleSubmit.bind(this);
    }

    public componentDidMount(): void {
        const user = this.props.context && this.props.context.request && this.props.context.request.user;
        if (!user.isAuthenticated) {
            this.setState({
                errorMessage: this.props.resources.unauthorizedUserMessage2,
                // inputState: false
            });
        }
    }

    public render(): JSX.Element | null {
        if (!(this.props && this.props.resources)) {
            return null;
        }
        return (
            <Form className='gift-card-balance' onSubmit={this._handleSubmit}>
                <div className='gift-card-balance-label'>
                    <label id='gift-card-balance-label' className='gift-card-balance-label-text'>
                        {this.props.resources.giftCardLableText}
                    </label>
                </div>
                <div className='gift-card-balance-input'>
                    <input
                        ref={this._giftCardNumberInputRef}
                        type='text'
                        onChange={this.onChange}
                        className='gift-card-balance-input-text'
                        aria-label={this.props.resources.giftCardLableText}
                        autoComplete='off'
                        // disabled={this.state.inputState}
                        value={this.state.giftCardNumber}
                        //aria-labelledby='checkout-gift-card__label checkout-gift-card__error'
                    />
                </div>
                <div className='gift-card-balance-error'>
                    {this.state && this.state.errorMessage && (
                        this.state.errorMessage.includes('sign in') ? this._getUnauthorizedTagMessage() :
                            <span id='checkout-gift-card__error' className='gift-card-balance-error-message' role='alert' aria-live='assertive'>
                                {this.state.errorMessage}
                            </span>
                    )}
                </div>
                <div className='gift-card-balance-message'>
                    {this.state && this.state.giftCardMessage && (
                        <span className='gift-card-balance-message-text'>
                            {this.state.giftCardMessage}
                        </span>
                    )}
                </div>
                <div>
                    <CallToAction
                        className='gift-card-balance-button'
                        title={this.props.resources.giftCardButtonText}
                        color='primary'
                        onClick={this._handleSubmit}
                        aria-label={this.props.resources.giftCardButtonText}
                        disabled={this.state.giftCardNumber ? false : true}
                    />
                </div>
            </Form>
        );
    }

    private onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ giftCardNumber: e.target.value || '', errorMessage: '' });
    };

    private _handleSubmit(e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | React.FormEvent<HTMLFormElement>): void {
        const giftCardNumber = this.state.giftCardNumber ? this.state.giftCardNumber.replace(/\s/g, '') : '';
        e.preventDefault();
        if (!giftCardNumber || (giftCardNumber && giftCardNumber.length === 0)) {
            this.setError(this.props.resources.emptyInputError);
        } else {
            this.setState({
                isFetchingGiftCard: true,
                errorMessage: '',
                giftCardMessage: ''
            });
            void this._getGiftCardBalance(giftCardNumber);
        }
    }

    private _getGiftCardBalance = (giftCardNumber: string) => {
        return getGiftCardInquiryAsync({ callerContext: this.props.context.actionContext }, giftCardNumber).then(activeGiftCard => {
            if (activeGiftCard instanceof Error) {
                throw activeGiftCard;
            }
            if (activeGiftCard && activeGiftCard.Id) {
                this.setState({
                    giftCardMessage: this._getGiftCardBalanceMessage(activeGiftCard.Id.replace(/\s/g, ''), activeGiftCard.BalanceInCardCurrency, activeGiftCard.BalanceCurrencyCode!)
                });
            }
        }).catch(error => {
            this.setError(format(this.props.resources.invalidGiftCardErrorMessage, giftCardNumber));
        }).finally(() => {
            this.setState({
                isFetchingGiftCard: false
            });
        });
    };

    private setError = (errorMessage: string): void => {
        this.setState({
            errorMessage
        });
        const input =
            this._giftCardNumberInputRef && this._giftCardNumberInputRef.current && this._giftCardNumberInputRef.current.focus && (this._giftCardNumberInputRef.current as HTMLElement);
        input && input.focus();
    };

    private _getFormattedPrice = (price: number = 0, currencyCode: string = 'USD'): string => {
        return this.props.context.cultureFormatter.formatCurrency(price, currencyCode);
    };

    private _getGiftCardBalanceMessage = (giftCardId: string, giftCardBalanceAmount: number = 0, giftCardCurrencyCode: string): string | undefined => {
        if (giftCardId && (giftCardBalanceAmount || giftCardBalanceAmount === 0)) {
            const giftCardLastFour: string | undefined = giftCardId && giftCardId.substring(giftCardId.length - 4, giftCardId.length) ? giftCardId.substring(giftCardId.length - 4, giftCardId.length) : undefined;
            const giftCardBalance: number = giftCardBalanceAmount ? giftCardBalanceAmount : 0;
            const giftCardCurrency: string | undefined = giftCardCurrencyCode ? giftCardCurrencyCode : 'USD';
            return format(this.props.resources.giftCardBalanceMessage, giftCardLastFour!, this._getFormattedPrice(giftCardBalance, giftCardCurrency));
        }
        return undefined;
    };

    private _getUnauthorizedTagMessage(): JSX.Element | null {
        const signInUrl = this.props.context.request.user.signInUrl;
        return (
            <span id='checkout-gift-card__error' className='gift-card-balance-error-message' role='alert' aria-live='assertive'>
                {this.props.resources.unauthorizedUserMessage1}{` `}
                <a className='gift-card-balance-error-signin' href={signInUrl}>{this.props.resources.unauthorizedUserMessage2}</a>{` `}
                {this.props.resources.unauthorizedUserMessage3}</span>
        );
    }
}

export default GiftCardBalance;
